import PropTypes from "prop-types";
import GridBlock from "components/GridBlock";
import React, {useCallback, useMemo, useReducer, useState} from "react";
import OfferChoiceCreateAuctionPopup from "./OfferChoiceCreateAuctionPopup";
import {AuctionType} from "constants/AuctionType";
import AuctionTypePart from "./AuctionTypePart";
import EditDatesAuctionPopup from "./EditDatesAuctionPopup";
import AuctionService from "services/AuctionService";
import ConfirmAuction, {createConfirmAuctionCfg} from "./ConfirmAuction";
import AuctionSaveError from "./AuctionSaveError";
import isResponseValid from "libs/isResponseValid";
import iconWorksReducer from "libs/iconWorksReducer";
import OfferChoiceService from "services/OfferChoiceService";
import redirectUsingPOST from "libs/redirectUsingPOST";
import WorkableButton from "components/button/WorkableButton";
import Tr from "translations/Translations";
import {makeStyles} from "@material-ui/core";

const useStyles = makeStyles(theme => ({
    inviteButton: {
        float: 'right',
        marginTop: theme.spacing()
    }
}));

//to widzimy tylko w statusie OFFER_COLLECTION i OFFER_DECRYPTION
function OfferChoiceSummaryCellAuction(
    {
        demandId,
        editable,
        working,
        rowWorking,
        offerChoiceRow,
        refreshRow,
        serverBrowserTimeDiff
    }) {
    const classes = useStyles();
    const currentAdditionalRoundInfo = useMemo(() => offerChoiceRow.currentAdditionalRoundInfo, [offerChoiceRow]);
    const [createPopupAuctionType, setCreatePopupAuctionType] = useState(null);
    const [editPopupAuction, setEditPopupAuction] = useState(null);
    const [auctionError, setAuctionError] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState(null);
    const [downloadInvitationWorking, setDownloadInvitationWorking] = useState(null);

    const [prodButtonWorks, dispatchProdButtonWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        publish: false,
        unpublish: false,
        remove: false,
        cancel: false,
        pause: false,
        accept: false,
        resume: false
    });
    const [testButtonWorks, dispatchTestButtonWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        publish: false,
        unpublish: false,
        remove: false,
        cancel: false,
        pause: false,
        accept: false,
        resume: false
    });

    const dispatchButtonWorks = (auction, type, works) => {
        if (auction.type === AuctionType.PROD) {
            dispatchProdButtonWorks({type, works})
        } else {
            dispatchTestButtonWorks({type, works})
        }
    }

    const {prod, test} = useMemo(() => currentAdditionalRoundInfo.auctions.reduce((prev, cur) => {
        if (cur.type === AuctionType.PROD)
            prev.prod.push(cur);
        else
            prev.test.push(cur);
        return prev;
    }, {prod: [], test: []}), [currentAdditionalRoundInfo]);


    const deleteAuction = useCallback((auction, confirmed, values) => {
        if (confirmed) {
            dispatchButtonWorks(auction, 'remove', true)
            setConfirmCfg(null);
            AuctionService.remove(demandId, currentAdditionalRoundInfo.roundId, values).then((resp) => {
                if (isResponseValid(resp, setAuctionError)) {
                    OfferChoiceService.removeAuctionFromAdditionalRoundInfo(offerChoiceRow.identity, auction);
                }
            }).finally(() => dispatchButtonWorks(auction, 'remove', false));
        } else {
            const callback = (auction, confirmed) => (values) => deleteAuction(auction, confirmed, values)
            setConfirmCfg(createConfirmAuctionCfg(auction, undefined, callback(auction, true), "auction_delete_confirm", "delete"));
        }
    }, [demandId, currentAdditionalRoundInfo, offerChoiceRow]);

    const editAuction = useCallback((auction) => {
        setEditPopupAuction(auction);
    }, []);

    const publishAuction = useCallback((auction, confirmed, values) => {
        if (confirmed) {
            dispatchButtonWorks(auction, 'publish', true)
            setConfirmCfg(null);
            AuctionService.publish(demandId, currentAdditionalRoundInfo.roundId, values).then((resp) => {
                if (isResponseValid(resp, setAuctionError)) {
                    OfferChoiceService.updateAuctionInAdditionalRoundInfo(offerChoiceRow.identity, resp.result);
                }
            }).finally(() => dispatchButtonWorks(auction, 'publish', false));
        } else {
            const callback = (auction, confirmed) => (values) => publishAuction(auction, confirmed, values)
            setConfirmCfg(createConfirmAuctionCfg(auction, true, callback(auction, true), "auction_publish_confirm", "publish"));
        }
    }, [demandId, currentAdditionalRoundInfo, offerChoiceRow]);

    const resumeAuction = useCallback((auction, confirmed, values) => {
        if (confirmed) {
            dispatchButtonWorks(auction, 'resume', true)
            setConfirmCfg(null);
            AuctionService.resume(demandId, currentAdditionalRoundInfo.roundId, values).then((resp) => {
                if (isResponseValid(resp, setAuctionError)) {
                    OfferChoiceService.updateAuctionInAdditionalRoundInfo(offerChoiceRow.identity, resp.result);
                }
            }).finally(() => dispatchButtonWorks(auction, 'resume', false));
        } else {
            const callback = (auction, confirmed) => (values) => resumeAuction(auction, confirmed, values)
            setConfirmCfg(createConfirmAuctionCfg(auction, true, callback(auction, true), "auction_resume_confirm", "resume"));
        }
    }, [demandId, currentAdditionalRoundInfo, offerChoiceRow]);

    const unpublishAuction = useCallback((auction, confirmed, values) => {
        if (confirmed) {
            dispatchButtonWorks(auction, 'unpublish', true)
            setConfirmCfg(null);
            AuctionService.unpublish(demandId, currentAdditionalRoundInfo.roundId, values).then((resp) => {
                if (isResponseValid(resp, setAuctionError)) {
                    OfferChoiceService.updateAuctionInAdditionalRoundInfo(offerChoiceRow.identity, resp.result);
                }
            }).finally(() => dispatchButtonWorks(auction, 'unpublish', false));
        } else {
            const callback = (auction, confirmed) => (values) => unpublishAuction(auction, confirmed, values)
            setConfirmCfg(createConfirmAuctionCfg(auction, true, callback(auction, true), "auction_unpublish_confirm", "unpublish"));
        }
    }, [demandId, currentAdditionalRoundInfo, offerChoiceRow]);

    const cancelAuction = useCallback((auction, confirmed, values) => {
        if (confirmed) {
            dispatchButtonWorks(auction, 'cancel', true)
            setConfirmCfg(null);
            AuctionService.cancel(demandId, currentAdditionalRoundInfo.roundId, values).then((resp) => {
                if (isResponseValid(resp, setAuctionError)) {
                    OfferChoiceService.updateAuctionInAdditionalRoundInfo(offerChoiceRow.identity, resp.result);
                }
            }).finally(() => dispatchButtonWorks(auction, 'cancel', false));
        } else {
            const callback = (auction, confirmed) => (values) => cancelAuction(auction, confirmed, values)
            setConfirmCfg(createConfirmAuctionCfg(auction, undefined, callback(auction, true), "auction_cancel_confirm", "auction_cancel"));
        }
    }, [demandId, currentAdditionalRoundInfo, offerChoiceRow]);

    const pauseAuction = useCallback((auction, confirmed, values) => {
        if (confirmed) {
            dispatchButtonWorks(auction, 'pause', true)
            setConfirmCfg(null);
            AuctionService.pause(demandId, currentAdditionalRoundInfo.roundId, values).then((resp) => {
                if (isResponseValid(resp, setAuctionError)) {
                    OfferChoiceService.updateAuctionInAdditionalRoundInfo(offerChoiceRow.identity, resp.result);
                }
            }).finally(() => dispatchButtonWorks(auction, 'pause', false));
        } else {
            const callback = (auction, confirmed) => (values) => pauseAuction(auction, confirmed, values)
            setConfirmCfg(createConfirmAuctionCfg(auction, true, callback(auction, true), "auction_pause_confirm", "pause"));
        }
    }, [demandId, currentAdditionalRoundInfo, offerChoiceRow]);

    const acceptAuction = useCallback((auction) => {

        dispatchButtonWorks(auction, 'accept', true);
        AuctionService.accept(demandId, currentAdditionalRoundInfo.roundId, auction.auctionId).then((resp) => {
            if (isResponseValid(resp, setAuctionError)) {
                redirectUsingPOST(resp.result);
            }
        }).finally(() => dispatchButtonWorks(auction, 'accept', false));


    }, [demandId, currentAdditionalRoundInfo]);

    const auctionDetails = useCallback((auction) => {
        AuctionService.details(demandId, currentAdditionalRoundInfo.roundId, auction.auctionId).then((resp) => {
            if (isResponseValid(resp, setAuctionError)) {
                OfferChoiceService.updateAuctionInAdditionalRoundInfo(offerChoiceRow.identity, resp.result);
            }
        })
    }, [demandId, currentAdditionalRoundInfo, offerChoiceRow]);

    const downloadAuctionInvitation = () => {
        setDownloadInvitationWorking(true);
        AuctionService.downloadAuctionInvitation(demandId, currentAdditionalRoundInfo.roundId, () => setDownloadInvitationWorking(false));
    }


    return (<GridBlock
        column
        justify='space-between'
        alignItems='center'
    >
        <AuctionTypePart
            demandId={demandId}
            editable={editable}
            working={working || rowWorking}
            auctionType={AuctionType.PROD}
            auctions={prod}
            buttonWorks={prodButtonWorks}
            setCreatePopupOpened={setCreatePopupAuctionType}
            acceptAuction={acceptAuction}
            cancelAuction={cancelAuction}
            deleteAuction={deleteAuction}
            editAuction={editAuction}
            pauseAuction={pauseAuction}
            publishAuction={publishAuction}
            unpublishAuction={unpublishAuction}
            resumeAuction={resumeAuction}
            auctionDetails={auctionDetails}
            serverBrowserTimeDiff={serverBrowserTimeDiff}
        />
        <br/>
        <AuctionTypePart
            demandId={demandId}
            editable={editable}
            working={working || rowWorking}
            auctionType={AuctionType.TEST}
            auctions={test}
            buttonWorks={testButtonWorks}
            setCreatePopupOpened={setCreatePopupAuctionType}
            cancelAuction={cancelAuction}
            deleteAuction={deleteAuction}
            editAuction={editAuction}
            pauseAuction={pauseAuction}
            publishAuction={publishAuction}
            unpublishAuction={unpublishAuction}
            resumeAuction={resumeAuction}
            auctionDetails={auctionDetails}
            serverBrowserTimeDiff={serverBrowserTimeDiff}
            prodAuctionStatus={prod.length > 0 ? prod[prod.length - 1].status : undefined}
        />

        {editable && prod.filter(a => a.status === 'INCOMPLETE').length > 0 && <WorkableButton
            className={classes.inviteButton}
            working={downloadInvitationWorking}
            variant='text'
            disabled={working || rowWorking}
            onClick={downloadAuctionInvitation}
        >{Tr('auction_invitation_button')}</WorkableButton>
        }

        {
            !!createPopupAuctionType && <OfferChoiceCreateAuctionPopup
                demandId={demandId}
                onSuccess={(auction) => {
                    OfferChoiceService.addAuctionToAdditionalRoundInfo(offerChoiceRow.identity, auction);
                    setCreatePopupAuctionType(null);
                }}
                onCancel={() => setCreatePopupAuctionType(null)}
                auctionType={createPopupAuctionType}
                roundId={currentAdditionalRoundInfo.roundId}
            />
        }
        {
            !!editPopupAuction && <EditDatesAuctionPopup
                demandId={demandId}
                roundId={currentAdditionalRoundInfo.roundId}
                offerChoiceRow={offerChoiceRow}
                onSuccess={() => setEditPopupAuction(null)}
                onCancel={() => setEditPopupAuction(null)}
                auction={editPopupAuction}
            />
        }
        {
            !!confirmCfg && <ConfirmAuction onCancel={() => setConfirmCfg(null)} cfg={confirmCfg}/>
        }
        {
            !!auctionError && <AuctionSaveError onClose={() => setAuctionError(null)} message={auctionError}/>
        }

    </GridBlock>)
}

OfferChoiceSummaryCellAuction.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    editable: PropTypes.bool.isRequired,
    working: PropTypes.bool.isRequired,
    rowWorking: PropTypes.bool.isRequired,
    offerChoiceRow: PropTypes.object.isRequired,
    refreshRow: PropTypes.func.isRequired,
    serverBrowserTimeDiff: PropTypes.object.isRequired,
}

export default OfferChoiceSummaryCellAuction;
